import React, { useState, useMemo } from 'react';
import { colorFromString } from '../utils/colors';

const SortableHeader = ({ children, columnKey, sortConfig, onSort }) => {
  const isSorted = sortConfig.key === columnKey;
  const direction = isSorted ? sortConfig.direction : 'none';

  const handleClick = () => {
    onSort(columnKey);
  };

  const arrow = direction === 'ascending' ? ' ▲' : direction === 'descending' ? ' ▼' : '';

  return (
    <th scope="col" onClick={handleClick} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {children}{arrow}
    </th>
  );
};

const HistoricalRanking = ({ data, onShowDetails }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'descending' });

  const historicalRanking = useMemo(() => {
    if (data.length === 0) return [];

    const indicators = [...new Set(data.map(item => item.indicador))];
    const dates = [...new Set(data.map(item => item.fecha.slice(0, 10)))];
    
    const businessDays = dates.filter(dateString => {
      const date = new Date(dateString);
      const day = date.getUTCDay();
      return day >= 1 && day <= 5;
    });

    const ranking = indicators.map(indicator => {
      const indicatorData = data.filter(item => item.indicador === indicator);
      const total = indicatorData.reduce((sum, item) => sum + parseInt(item.valor, 10), 0);
      const daysWithRecords = new Set(indicatorData.map(item => item.fecha.slice(0, 10))).size;
      const avgPerDay = dates.length > 0 ? total / dates.length : 0;
      const avgPerBusinessDay = businessDays.length > 0 ? total / businessDays.length : 0;
      const avgPerDayWithRecords = daysWithRecords > 0 ? total / daysWithRecords : 0;

      return {
        indicator,
        total,
        daysWithRecords,
        avgPerDay: parseFloat(avgPerDay.toFixed(2)),
        avgPerBusinessDay: parseFloat(avgPerBusinessDay.toFixed(2)),
        avgPerDayWithRecords: parseFloat(avgPerDayWithRecords.toFixed(2)),
      };
    });

    return ranking;
  }, [data]);

  const sortedRanking = useMemo(() => {
    let sortableItems = [...historicalRanking];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [historicalRanking, sortConfig]);

  const handleSort = (key) => {
    const isSameKey = sortConfig.key === key;
    let direction = 'descending'; // Default to descending for a new column

    if (isSameKey) {
      // If it's the same column, toggle from the current direction
      direction = sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
    }
    
    setSortConfig({ key, direction });
  };

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-body">
        <h5 className="card-title text-center mb-3">Ranking Histórico</h5>
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead className="table-dark">
              <tr>
                <th scope="col">#</th>
                <SortableHeader columnKey="indicator" sortConfig={sortConfig} onSort={handleSort}>Usuario</SortableHeader>
                <SortableHeader columnKey="total" sortConfig={sortConfig} onSort={handleSort}>Total</SortableHeader>
                <SortableHeader columnKey="daysWithRecords" sortConfig={sortConfig} onSort={handleSort}>Días con Registros</SortableHeader>
                <SortableHeader columnKey="avgPerDayWithRecords" sortConfig={sortConfig} onSort={handleSort}>Promedio por Día con Registros</SortableHeader>
                <SortableHeader columnKey="avgPerDay" sortConfig={sortConfig} onSort={handleSort}>Promedio por Día</SortableHeader>
                <SortableHeader columnKey="avgPerBusinessDay" sortConfig={sortConfig} onSort={handleSort}>Promedio por Día Hábil</SortableHeader>
                <th scope="col">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sortedRanking.map((item, index) => (
                <tr key={item.indicator}>
                  <th scope="row">{index + 1}</th>
                  <td>
                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: colorFromString(item.indicator), marginRight: '8px' }}></span>
                    {item.indicator}
                  </td>
                  <td>{item.total}</td>
                  <td>{item.daysWithRecords}</td>
                  <td>{item.avgPerDayWithRecords}</td>
                  <td>{item.avgPerDay}</td>
                  <td>{item.avgPerBusinessDay}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => onShowDetails(item.indicator)} title="Ver Productividad">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M.5 0a.5.5 0 0 1 .5.5v15a.5.5 0 0 1-1 0V.5A.5.5 0 0 1 .5 0zM2 1.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-4a.5.5 0 0 1-.5-.5v-1zm2 4a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-1zm2 4a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-6a.5.5 0 0 1-.5-.5v-1zm2 4a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-1z"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HistoricalRanking;
